import { Suspense, useEffect, useMemo, useRef } from "react";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { usePressClassificationRoles } from "@libs/roles/hooks/usePressClassificationRoles";
import { usePressIdFromURL } from "@utils/getStateFromURL";

import * as S from "./UnsuitableButton.style";
import { useCheckReviewRequired } from "../../hooks/useCheckReviewRequired";
import { useHandleUnsuitablePress } from "../../hooks/useHandleUnsuitablePress";
import {
  UnsuitableReasonModalPosition,
  UnsuitableReasonUsageType,
} from "../../types/unsuitableReason.types";
import UnsuitableReasonModalContainer from "../UnsuitableReasonModalContainer/UnsuitableReasonModalContainer";

type Props = {
  type?: UnsuitableReasonUsageType;
  modalPosition?: UnsuitableReasonModalPosition;
};

const UnsuitableButton = ({ type = "PRESS_DETAIL", modalPosition }: Props) => {
  const { isUnsuitableWithReviewRequired } = useCheckReviewRequired();
  const draftArticleId = usePressIdFromURL();
  const { data: articleInfo } = useArticleInfoQuery(Number(draftArticleId));
  const unsuitableRef = useRef<HTMLDivElement>(null);

  const { hasUnsuitablePressRoles } = usePressClassificationRoles();

  const TYPE_TO_TEXT: Record<UnsuitableReasonUsageType, string> = {
    PRESS_DETAIL: isUnsuitableWithReviewRequired
      ? "Unsuitable / Review Required"
      : "Unsuitable",
    CTG_DETAIL: "The Unsuitable Registry",
    PRESS_LIST: "The Unsuitable Press",
    CTG_LIST: "The Unsuitable Registry",
    PUBLICATION_DETAIL: "The Unsuitable Publication",
    PUBLICATION_LIST: "The Unsuitable Publication",
  };

  const ROLES_BY_TYPE = {
    PRESS_DETAIL: hasUnsuitablePressRoles,
    CTG_DETAIL: hasUnsuitablePressRoles,
    PRESS_LIST: hasUnsuitablePressRoles,
    CTG_LIST: hasUnsuitablePressRoles,
    PUBLICATION_DETAIL: hasUnsuitablePressRoles,
    PUBLICATION_LIST: hasUnsuitablePressRoles,
  };

  const duplArticleId = articleInfo?.duplArticleId || null;
  const articleRejectReason = articleInfo?.articleRejectReason || null;
  const isReviewRequired =
    ["PRESS_DETAIL", "PRESS_LIST"].includes(type) &&
    articleInfo?.isReviewRequired === true &&
    isUnsuitableWithReviewRequired;
  const isRejectedReason = !!articleRejectReason && !isReviewRequired;

  const targetRejectReason = articleRejectReason
    ? `${articleRejectReason}${duplArticleId ? ` (${duplArticleId})` : ""}`
    : null;

  const {
    openModalUnsuitablePress,
    closeModalUnsuitablePress,
    isOpenModalUnsuitablePress,
  } = useHandleUnsuitablePress({ type });

  const handleUnsuitableButtonActive = async () => {
    if (isOpenModalUnsuitablePress) return closeModalUnsuitablePress();

    openModalUnsuitablePress();
  };

  useEffect(() => {
    const handleCheckClickOutsideUnsuitable = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickReasonButton = unsuitableRef.current?.contains(target);
      const deleteReasonButton = target.dataset.button;

      if (clickReasonButton) return;
      if (deleteReasonButton) return;

      closeModalUnsuitablePress();
    };

    window.addEventListener("click", handleCheckClickOutsideUnsuitable);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideUnsuitable);
  }, [closeModalUnsuitablePress]);

  const unsuitableButtonText = useMemo(() => {
    if (isRejectedReason) return targetRejectReason;
    if (isReviewRequired)
      return `Overlapped Id: #${articleInfo.reviewIdList?.join(
        ", #",
      )} (Review Required)`;
    return TYPE_TO_TEXT[type];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRejectedReason,
    targetRejectReason,
    isReviewRequired,
    type,
    articleInfo.reviewIdList,
  ]);

  return (
    <>
      <S.UnsuitableButtonWrapper
        ref={unsuitableRef}
        isEnableUnsuitablePress={isRejectedReason}
        disabled={!hasUnsuitablePressRoles}
      >
        <S.UnsuitableButton
          type="button"
          onClick={handleUnsuitableButtonActive}
          isEnableUnsuitablePress={isRejectedReason}
          disabled={!ROLES_BY_TYPE[type]}
          isReviewRequired={isReviewRequired}
        >
          {unsuitableButtonText}
        </S.UnsuitableButton>
        <Suspense fallback={<></>}>
          <UnsuitableReasonModalContainer
            closeModalUnsuitablePress={closeModalUnsuitablePress}
            isOpenModalUnsuitablePress={isOpenModalUnsuitablePress}
            articleRejectReason={articleRejectReason}
            unsuitableReasonUsageType={type}
            draftArticleId={draftArticleId}
            modalPosition={modalPosition}
          />
        </Suspense>
      </S.UnsuitableButtonWrapper>
    </>
  );
};

export default UnsuitableButton;
